// @ts-nocheck
import React, {useState} from 'react';
import {Box, Card, CardContent, CardHeader, Divider, IconButton, TextField, Typography} from '@mui/material';
import {t} from 'ttag';
import {useFormContext, useWatch} from 'react-hook-form';
import {DataGrid} from '@mui/x-data-grid';
import {Delete} from '@mui/icons-material';
import AddRowBtn from './AddRowButton';
import ConfirmDialog from '@components/common/ConfirmDialog';
import table from '../../../../../../../../../../components/common/styles';

const readOnlyColumns = [
  {
    field: 'route',
    headerName: t`Route`,
    width: 100,
    flex: 1,
    sortable: false,
    disableColumnMenu: true
  },
  {
    field: 'flight_number',
    headerName: t`Flight number`,
    width: 100,
    flex: 1,
    sortable: false,
    disableColumnMenu: true,
    cellClassName: 'secondary-text'
  },
  {
    field: 'fare_basis',
    headerName: t`Fare basis`,
    width: 100,
    flex: 1,
    sortable: false,
    disableColumnMenu: true,
    cellClassName: 'secondary-text'
  },
  {
    field: 'coupon_status',
    headerName: t`Coupon status`,
    width: 100,
    flex: 1,
    sortable: false,
    disableColumnMenu: true
  },
  {
    field: 'date',
    headerName: t`Date`,
    width: 100,
    flex: 1,
    headerAlign: 'right',
    align: 'right',
    sortable: false,
    disableColumnMenu: true
  }
];

const cellSx = {'& .MuiInputBase-input': {fontSize: '0.875rem', pl: 1.15, pt: 0.7}};

const RefundSubjectRoutes = () => {
  const {setValue} = useFormContext();
  const [coupons, manual] = useWatch({name: ['coupons', 'manual']});
  const [couponToDelete, setCouponToDelete] = useState(null);

  if (!coupons) return null;

  if (manual) {
    const editHandler = ({field, value, id}) => {
      if (field === 'route') {
        const parts = value.toUpperCase().replace(/\s/g, '').split('-');
        setValue('coupons', coupons.map(c => c.localId === id
          ? {...c, originAirport: (parts[0] || '').slice(0, 3), destinationAirport: (parts[1] || '').slice(0, 3)}
          : c
        ), {shouldValidate: true});
      } else {
        setValue('coupons', coupons.map(c => c.localId === id ? {...c, [field]: value} : c), {shouldValidate: true});
      }
    };

    const createHandler = () => {
      setValue('coupons', [...coupons, {
        localId: coupons.length,
        originAirport: '', destinationAirport: '', carrier: '',
        flightNumber: '', fareBasis: '', couponStatus: 'O', datetime: '',
      }], {shouldValidate: true});
    };

    const removeHandler = () => {
      setValue('coupons', coupons.filter(c => c.localId !== couponToDelete.localId), {shouldValidate: true});
      setCouponToDelete(null);
    };

    const editableColumns = [
      {
        field: 'route',
        headerName: t`Route`,
        editable: true,
        flex: 1,
        sortable: false,
        disableColumnMenu: true,
        valueGetter: (params) => `${params.row.originAirport || ''}-${params.row.destinationAirport || ''}`,
        renderEditCell: (params) => (
          <TextField
            variant='standard'
            size='small'
            value={params.value}
            placeholder='LIS-OPO'
            inputProps={{maxLength: 7}}
            InputProps={{disableUnderline: true}}
            sx={cellSx}
            autoFocus
            fullWidth
            onChange={(e) => {
              const val = e.target.value.toUpperCase().replace(/[^A-Z-]/g, '');
              params.api.setEditCellValue({id: params.id, field: params.field, value: val});
              editHandler({id: params.id, field: 'route', value: val});
            }}
          />
        ),
      },
      {
        field: 'flightNumber',
        headerName: t`Flight number`,
        editable: true,
        flex: 1,
        sortable: false,
        disableColumnMenu: true,
        valueGetter: (params) => params.row.flightNumber || '',
        renderEditCell: (params) => (
          <TextField
            variant='standard'
            size='small'
            value={params.value}
            InputProps={{disableUnderline: true}}
            sx={cellSx}
            autoFocus
            fullWidth
            onChange={(e) => {
              const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
              params.api.setEditCellValue({id: params.id, field: params.field, value: val});
              editHandler({id: params.id, field: 'flightNumber', value: val});
            }}
          />
        ),
      },
      {
        field: 'fareBasis',
        headerName: t`Fare basis`,
        editable: true,
        flex: 1,
        sortable: false,
        disableColumnMenu: true,
        valueGetter: (params) => params.row.fareBasis || '',
        renderEditCell: (params) => (
          <TextField
            variant='standard'
            size='small'
            value={params.value}
            InputProps={{disableUnderline: true}}
            sx={cellSx}
            autoFocus
            fullWidth
            onChange={(e) => {
              const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
              params.api.setEditCellValue({id: params.id, field: params.field, value: val});
              editHandler({id: params.id, field: 'fareBasis', value: val});
            }}
          />
        ),
      },
      {
        field: 'couponStatus',
        headerName: t`Coupon status`,
        editable: true,
        flex: 0.8,
        sortable: false,
        disableColumnMenu: true,
        valueGetter: (params) => params.row.couponStatus || 'O',
        renderEditCell: (params) => (
          <TextField
            variant='standard'
            size='small'
            value={params.value}
            inputProps={{maxLength: 1}}
            InputProps={{disableUnderline: true}}
            sx={cellSx}
            autoFocus
            fullWidth
            onChange={(e) => {
              const val = e.target.value.toUpperCase().replace(/[^A-Z]/g, '');
              params.api.setEditCellValue({id: params.id, field: params.field, value: val});
              editHandler({id: params.id, field: 'couponStatus', value: val});
            }}
          />
        ),
      },
      {
        field: 'datetime',
        headerName: t`Date`,
        editable: true,
        flex: 1.5,
        sortable: false,
        disableColumnMenu: true,
        valueGetter: (params) => params.row.datetime || '',
        renderEditCell: (params) => {
          const inputValue = params.value ? params.value.slice(0, 16).replace(' ', 'T') : '';
          return (
            <TextField
              variant='standard'
              type='datetime-local'
              size='small'
              value={inputValue}
              InputProps={{disableUnderline: true}}
              sx={cellSx}
              fullWidth
              onChange={(e) => {
                const val = e.target.value ? e.target.value.replace('T', ' ') + ':00' : '';
                params.api.setEditCellValue({id: params.id, field: params.field, value: val});
                editHandler({id: params.id, field: 'datetime', value: val});
              }}
            />
          );
        },
      },
      {
        field: 'actions',
        headerName: t`Actions`,
        flex: 0.5,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Box>
            <IconButton
              onClick={() => setCouponToDelete(params.row)}
              disabled={coupons.length <= 1}
              size='small'
              aria-label='delete'
            >
              <Delete fontSize='inherit' />
            </IconButton>
          </Box>
        ),
      },
    ];

    return (
      <Card elevation={3}>
        <CardHeader
          title={<Typography fontFamily={'OpenSansMedium'}>{t`Itinerary`}</Typography>}
          action={<AddRowBtn onClick={createHandler}/>}
        />
        <Divider/>
        <CardContent>
          <DataGrid
            autoHeight
            density={'compact'}
            getRowId={({localId}) => localId}
            columns={editableColumns}
            rows={coupons}
            initialState={{pagination: {paginationModel: {pageSize: 100}}}}
            pageSizeOptions={[100]}
            disableRowSelectionOnClick
            hideFooter
            sx={table}
          />
          {Boolean(couponToDelete) && (
            <ConfirmDialog
              title={t`Itinerary`}
              cancelText={t`Cancel`}
              confirmText={t`Delete`}
              variant={'error'}
              onConfirm={removeHandler}
              onCancel={() => setCouponToDelete(null)}
              reverse
            >
              {t`Do you really want to remove this route?`}
            </ConfirmDialog>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card elevation={3}>
      <CardHeader title={<Typography fontFamily={'OpenSansMedium'}>{t`Itinerary`}</Typography>}/>
      <Divider/>
      <CardContent>
        <DataGrid
          columns={readOnlyColumns}
          rows={coupons.map((c, idx) => ({
            id: idx,
            route: `${c.originAirport || ''} - ${c.destinationAirport || ''}`,
            flight_number: c.flightNumber,
            fare_basis: c.fareBasis,
            coupon_status: c.couponStatus,
            date: c.datetime
          }))}
          density={'compact'}
          disableRowSelectionOnClick
          hideFooter
          sx={table}
        />
      </CardContent>
    </Card>
  );
};

export default RefundSubjectRoutes;